import { Injectable } from '@angular/core'
import { RootStoreState, UserProfileActions, UserProfileSelectors } from '../../root-store/root-index'
import { Store, select } from '@ngrx/store'
import { BehaviorSubject, Observable } from 'rxjs'
import { User } from '../../models/user'
import { Router } from '@angular/router'
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators'
import moment from 'moment'
import { LicenseService } from '../../services/license.service'

@Injectable({
  providedIn: 'root'
})
export class AuthFacadeService {
  userProfileError$: Observable<any>
  userProfileUser$: Observable<User>

  /** is the user currently in the login process (after pressing 'login', before processing of be-response done) */
  isInLogin$: Observable<boolean>
  /** time when the token gets invalidated */
  tokenValidUntil$: Observable<string>
  /** user profile exists and the currently stored token is not yet expired. (relevant distinction to userIsLoggedIn$ after application start) */
  tokenIsNotYetExpired$: Observable<boolean>
  /** user profile exists*/
  userIsLoggedIn$: Observable<boolean>

  isInLogout$: Observable<boolean>

  defaultStartRoute$: Observable<string>

  private $userIsCurrentlyActive = new BehaviorSubject<boolean>(true)
  /** true if the user is currently active (via mouse movement/click or keyboard input within the last minute) */
  userIsCurrentlyActive$ = this.$userIsCurrentlyActive.asObservable().pipe(
    distinctUntilChanged(),
    tap((isActive) => console.log('activity$ changed to: ' + isActive))
  )
  private _timerId: any

  constructor(
    private store$: Store<RootStoreState.State>,

    private licenseService: LicenseService,

    public router: Router
  ) {
    this.userProfileError$ = this.store$.pipe(select((state) => UserProfileSelectors.selectUserProfileError(state)))

    this.userProfileUser$ = this.store$.pipe(select((state) => UserProfileSelectors.selectUserProfileUser(state)))

    this.isInLogin$ = this.store$.pipe(select((state) => UserProfileSelectors.selectUserProfileIsLoading(state)))

    this.tokenValidUntil$ = this.store$.pipe(select((state) => UserProfileSelectors.selectValidUntil(state)))

    this.tokenIsNotYetExpired$ = this.tokenValidUntil$.pipe(
      map((tokenValidUntil) => !!tokenValidUntil && moment().isBefore(moment(tokenValidUntil)))
    )

    this.userIsLoggedIn$ = this.store$.pipe(select((state) => UserProfileSelectors.selectIsLoggedIn(state)))

    this.isInLogout$ = this.store$.pipe(select((state) => UserProfileSelectors.selectUserIsInLogout(state)))

    this.defaultStartRoute$ = this.store$.pipe(select((state) => UserProfileSelectors.selectDefaultStartRoute(state)))

    this.resetTimer()
  }

  login(userName: string, password: string) {
    this.store$.dispatch(UserProfileActions.loginRequested({ userName, password }))

    // initiating license check, after login is successfully completed.
    this.licenseService.checkExpirationDateAndShowWarningMessage(
      this.userIsLoggedIn$.pipe(filter((userIsLoggedIn) => userIsLoggedIn))
    )
  }

  logout(sendLogoutRequestToServer = false) {
    this.store$.dispatch(UserProfileActions.logoutRequested({ sendLogoutRequestToServer }))
  }

  validateToken() {
    // console.log('validating token');
    this.userProfileUser$
      .pipe(
        // unsubscribe after first result
        take(1)
      )
      .subscribe((user) => {
        if (!!user && !!user.token) {
          this.store$.dispatch(UserProfileActions.validateTokenRequested())
        } else {
          this.router.navigate(['/login'])
          console.log('no token stored.')
        }
      })
  }

  prolongToken() {
    this.store$.dispatch(UserProfileActions.prolongTokenRequested())
  }

  changePassword(oldPw: string, newPw: string) {
    this.store$.dispatch(UserProfileActions.changePwRequested({ oldPw, newPw }))
  }

  reactivateToken(userName: string, password: string) {
    this.store$.dispatch(UserProfileActions.reactivateTokenRequested({ userName, password }))
  }

  /** sets the user as active and resets the timer
   */
  userIsActive() {
    // console.log('user is active');
    this.$userIsCurrentlyActive.next(true)
    this.resetTimer()
  }

  /** sets the user as inactive */
  userIsInactive() {
    this.$userIsCurrentlyActive.next(false)
  }

  /** resets the timer for marking the user as inactive */
  resetTimer() {
    if (this._timerId) {
      clearTimeout(this._timerId)
    }
    this._timerId = setTimeout(() => this.userIsInactive(), 60 * 1000) // 1 minute
  }
}
